import { useCompanyPermissions } from './useCompanyPermissions'
import { CompanyUserRole } from '../core/models/Company'

export interface CompanyDisabledState {
  isDisabled: boolean
  canCreateRooms: boolean
  canJoinRooms: boolean
  canAccessDashboard: boolean
  canAccessCompanyManagement: boolean
  disabledReason: string | null
  companyName: string | null
  userRole: string | null
  isAdmin: boolean
  showDisabledAlert: boolean
  showCompanyManagementAccess: boolean
}

export const useCompanyDisabledState = (): CompanyDisabledState => {
  const {
    isCompanyDisabled,
    canCreateRooms,
    canJoinRooms,
    canAccessDashboard,
    canAccessCompanyManagement,
    disabledReason,
    currentCompany,
    isCompanyManagerOrAdmin,
    loading
  } = useCompanyPermissions()

  const isDisabled = isCompanyDisabled
  const companyName = currentCompany?.name || null
  const userRole = currentCompany?.user_role || null
  const isAdmin = isCompanyManagerOrAdmin || userRole === CompanyUserRole.ADMIN || userRole === CompanyUserRole.MANAGER
  
  // Show disabled alert if company is disabled and not loading
  const showDisabledAlert = isDisabled && !loading
  
  // Show company management access for admins even when disabled
  const showCompanyManagementAccess = isDisabled && isAdmin && canAccessCompanyManagement

  return {
    isDisabled,
    canCreateRooms,
    canJoinRooms,
    canAccessDashboard,
    canAccessCompanyManagement,
    disabledReason,
    companyName,
    userRole,
    isAdmin,
    showDisabledAlert,
    showCompanyManagementAccess
  }
}

// Utility function to check if user can perform specific actions
export const useCompanyActionPermissions = () => {
  const disabledState = useCompanyDisabledState()
  
  return {
    canCreateRoom: () => !disabledState.isDisabled && disabledState.canCreateRooms,
    canJoinRoom: () => !disabledState.isDisabled && disabledState.canJoinRooms,
    canAccessDashboard: () => !disabledState.isDisabled && disabledState.canAccessDashboard,
    canAccessCompanyManagement: () => disabledState.canAccessCompanyManagement,
    isCompanyDisabled: () => disabledState.isDisabled,
    getDisabledReason: () => disabledState.disabledReason,
    showDisabledAlert: () => disabledState.showDisabledAlert,
    showCompanyManagementAccess: () => disabledState.showCompanyManagementAccess
  }
} 